import React from 'react';
import Header from '../components/layout/Header';
import { Footer } from '../components/marketing';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <div className="pt-24 pb-16 px-4 md:pt-32 md:pb-24 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-display-lg font-display text-[#F5F1E8] uppercase mb-2">Privacy Policy</h1>
          <p className="text-xs font-mono text-[rgba(245,241,232,0.48)] mb-12 tracking-wider">LAST UPDATED: MARCH 2026</p>

          <div className="space-y-8">
            <Section title="1. Information We Collect">
              <h4>Account Information</h4>
              <p>Name, email address, and authentication credentials (or OAuth tokens from Google/GitHub). Enterprise customers: company name, role, and billing contact.</p>

              <h4>User Content</h4>
              <p>Debate prompts, context documents, uploaded files, and decision outputs you create using the Service.</p>

              <h4>Usage Data</h4>
              <p>Pages visited, features used, debate parameters, model selections, and performance metrics. Collected via first-party analytics only — we do not use third-party tracking pixels.</p>

              <h4>Payment Information</h4>
              <p>Processed and stored by Stripe. We do not store credit card numbers, CVVs, or full payment details on our servers.</p>
            </Section>

            <Section title="2. How We Use Your Information">
              <ul>
                <li>Providing, maintaining, and improving the Service</li>
                <li>Processing debates through AI model providers</li>
                <li>Sending account-related communications (transactional only — no marketing without consent)</li>
                <li>Analyzing aggregated, anonymized usage patterns to improve the product</li>
                <li>Responding to support requests</li>
                <li>Complying with legal obligations</li>
              </ul>
            </Section>

            <Section title="3. AI Model Data Processing">
              <p><strong>Training Opt-Out:</strong> All AI model providers (OpenAI, Anthropic, Google) process your debate content under data processing agreements that explicitly prohibit using your data for model training.</p>
              <p><strong>BYOK Mode:</strong> When using Bring Your Own Key mode, API calls go directly from your browser to the model provider. Fray does not intercept, store, or have access to model responses.</p>
              <p><strong>Standard Mode:</strong> Debate content is transmitted to model providers via our servers. We retain debate transcripts and outputs in your account. Model providers do not retain your data beyond the API request lifecycle.</p>
            </Section>

            <Section title="4. Data Sharing">
              <p>We do not sell your personal information. We share data only with:</p>
              <ul>
                <li><strong>AI Model Providers:</strong> To process debates (OpenAI, Anthropic, Google — all under DPAs)</li>
                <li><strong>Infrastructure Providers:</strong> Supabase (database), Vercel (hosting), Stripe (payments)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p>See our <a href="/security" className="text-[#00D4FF] hover:text-[#33DDFF]">Security page</a> for the complete sub-processor list.</p>
            </Section>

            <Section title="5. Data Retention">
              <ul>
                <li><strong>Active Accounts:</strong> Data retained for the duration of your account</li>
                <li><strong>Deleted Accounts:</strong> All User Content deleted within 30 days of account deletion</li>
                <li><strong>Backups:</strong> Encrypted backups retained for 90 days maximum, then purged</li>
                <li><strong>Audit Logs:</strong> Enterprise audit logs retained per your organization's policy (default: 1 year)</li>
              </ul>
            </Section>

            <Section title="6. Your Rights (GDPR/CCPA)">
              <p>You have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of all data we hold about you</li>
                <li><strong>Rectification:</strong> Correct inaccurate personal data</li>
                <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
                <li><strong>Portability:</strong> Export your debates and decision outputs in standard formats</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              </ul>
              <p>To exercise any right, email privacy@getfray.ai. We respond within 30 days.</p>
            </Section>

            <Section title="7. Cookies">
              <p>We use essential cookies for authentication and session management. We use one analytics cookie (first-party) for aggregated usage data. We do not use advertising cookies or third-party tracking.</p>
              <p>The mode preference cookie (fray_mode) stores your Work/Labs mode selection. It contains no personal information.</p>
            </Section>

            <Section title="8. Children's Privacy">
              <p>The Service is not directed to individuals under 16. We do not knowingly collect data from children.</p>
            </Section>

            <Section title="9. International Transfers">
              <p>Data is primarily processed in the United States. For EU users, we rely on Standard Contractual Clauses for international data transfers. Enterprise customers may request data residency restrictions.</p>
            </Section>

            <Section title="10. Contact">
              <p>Data Protection inquiries: privacy@getfray.ai</p>
              <p>Data Processing Agreement requests: <a href="/security" className="text-[#00D4FF] hover:text-[#33DDFF]">Security page</a></p>
            </Section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-sm font-mono text-[#F5F1E8] font-medium tracking-wider uppercase mb-3">{title}</h2>
      <div className="text-sm text-[rgba(245,241,232,0.72)] leading-relaxed font-body space-y-3 [&_h4]:text-[#F5F1E8] [&_h4]:font-mono [&_h4]:text-xs [&_h4]:tracking-wider [&_h4]:uppercase [&_h4]:mt-4 [&_h4]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-[rgba(245,241,232,0.64)] [&_strong]:text-[#F5F1E8]">
        {children}
      </div>
    </div>
  );
}
